/**
 * Billing ⊕ Entitlements → one SubscriptionState.
 *
 * /v1/billing/subscription is admin-only by contract, so a member's shell reads status from
 * /v1/entitlements/me instead. The two shapes differ in casing AND in coverage:
 *
 *                       Subscription (billing)   EntitlementSnapshot (entitlements)
 *   casing              camelCase                 snake_case
 *   seats               seatLimit/seatCount       seat_limit/seat_count + seat_valid
 *   status enum         6 values                  5 — no `incomplete`
 *   cancellation        cancelAtPeriodEnd         absent
 *   trial               trialEndsAt               trial_ends_at + trial_state
 *   extra               addons                    degraded (served from the grace tier)
 *
 * They are normalised ONCE, here, and the member view is treated as a SUBSET. The UI never
 * branches on which endpoint answered — `source` exists for degraded-capability decisions
 * only, and never reaches a layout.
 */
import type { Subscription } from "./generated/billing.generated.js";
import type { EntitlementSnapshot } from "./generated/entitlements.generated.js";

export type SubscriptionStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "locked"
  | "incomplete";

export type Plan = "individual" | "team" | "enterprise";
export type Product = "gateway" | "smart" | "guard" | "plan";

export interface SubscriptionState {
  status: SubscriptionStatus;
  plan: Plan;
  interval: "month" | "year" | null;
  seatLimit: number;
  seatCount: number;
  seatValid: boolean;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  /**
   * `false` on the member branch. That is an ABSENCE OF SIGNAL, not a claim — and nothing
   * depends on the difference, because a member never renders the "Ends {date}" pill or
   * any billing CTA. `source` records why.
   */
  cancelAtPeriodEnd: boolean;
  products: readonly Product[];
  /** For degraded-capability decisions ONLY. Never for layout. */
  source: "billing" | "entitlements";
  /** Entitlements-only: true = stale-but-safe, served from the Redis grace tier. */
  degraded: boolean;
}

export function fromBilling(s: Subscription): SubscriptionState {
  return {
    status: s.status,
    plan: s.plan,
    interval: s.interval ?? null,
    seatLimit: s.seatLimit,
    seatCount: s.seatCount,
    // Derived here because Billing does not send it. The formula is the contract's own —
    // do not redefine it differently in two places.
    seatValid: s.status !== "locked" && s.seatCount <= s.seatLimit,
    trialEndsAt: s.trialEndsAt ?? null,
    currentPeriodEnd: s.currentPeriodEnd ?? null,
    cancelAtPeriodEnd: s.cancelAtPeriodEnd ?? false,
    products: s.products,
    source: "billing",
    degraded: false,
  };
}

export function fromEntitlements(e: EntitlementSnapshot): SubscriptionState {
  return {
    // `incomplete` is unreachable on this branch by contract. The union keeps it; the
    // member path simply never produces it.
    status: e.status,
    plan: e.plan,
    // [CONTRACT] EntitlementSnapshot DOES carry `interval` — API_CLIENT.md §5.3's diff table
    // lists it as absent, which is stale. Reading it means a member's period never has to be
    // guessed, and the two branches produce the same field set.
    interval: e.interval ?? null,
    seatLimit: e.seat_limit,
    seatCount: e.seat_count,
    // READ, not derived, when present: the contract defines seat_valid and the server owns
    // it. It is OPTIONAL in the schema, so the same formula is the fallback — one
    // definition, applied in one place, rather than two that can drift.
    seatValid: e.seat_valid ?? (e.status !== "locked" && e.seat_count <= e.seat_limit),
    trialEndsAt: e.trial_ends_at ?? null,
    currentPeriodEnd: e.current_period_end ?? null,
    cancelAtPeriodEnd: false,
    products: e.products,
    source: "entitlements",
    degraded: e.degraded ?? false,
  };
}

/**
 * Days remaining in a trial, rounded up.
 *
 * daysLeft drives PRESENTATION. `status` drives ACCESS. The day-11/day-14 sweep is
 * server-side and cadence-dependent, so the two can disagree by up to one sweep interval —
 * and NEVER derive status from trialEndsAt: a converted org has a past trialEndsAt and is
 * perfectly active.
 */
export function daysLeft(trialEndsAt: string | null, now: Date = new Date()): number | null {
  if (!trialEndsAt) return null;
  const end = Date.parse(trialEndsAt);
  if (!Number.isFinite(end)) return null;
  return Math.ceil((end - now.getTime()) / 86_400_000);
}

/** `--warn` from three days out. No red, no timer: a calm reminder, never a countdown. */
export function trialTone(days: number | null): "success" | "warn" {
  return days !== null && days <= 3 ? "warn" : "success";
}

export function hasGuard(state: Pick<SubscriptionState, "products">): boolean {
  return state.products.includes("guard");
}

/** Over the limit is an outage for people who did nothing wrong; at the limit is a block. */
export type SeatState = "under" | "at" | "over";

export function seatState(s: Pick<SubscriptionState, "seatCount" | "seatLimit">): SeatState {
  if (s.seatCount > s.seatLimit) return "over";
  if (s.seatCount >= s.seatLimit) return "at";
  return "under";
}
